import {
  EToolStepStatus,
  type IAppendMessageToolStepProps,
  type ISetMessageToolStepStatusInPagesProps,
  type ISetMessageToolStepStatusProps,
} from "../types/chat";
import type {
  IFormatToolStepLabelProps,
  IIsToolCallShownAsStepProps,
  IResolveToolStepDisplayStatusProps,
  TToolStepDisplayStatus,
} from "./tool-steps.types";

// NOTE: NSAI-5473 — mirror the server's MAX_TOOL_STEPS_PER_MESSAGE,
// MAX_TOOL_STEP_ARG_KEYS and MAX_TOOL_STEP_ARG_LENGTH, so the live list and the
// one a reload brings back hold the same lines with the same arguments.
export const MAX_TOOL_STEPS_PER_MESSAGE = 100;
export const MAX_TOOL_STEP_ARG_KEYS = 12;
export const MAX_TOOL_STEP_ARG_LENGTH = 160;
// NOTE: NSAI-7892 — mirror the server's bounds on a structured arg: arrays and
// objects stay structured MAX_TOOL_STEP_ARG_DEPTH levels deep, an array keeps
// MAX_TOOL_STEP_ARG_ITEMS items, and their strings share one
// MAX_TOOL_STEP_NESTED_ARGS_LENGTH budget per step.
export const MAX_TOOL_STEP_ARG_DEPTH = 2;
export const MAX_TOOL_STEP_ARG_ITEMS = 10;
export const MAX_TOOL_STEP_NESTED_ARGS_LENGTH = 4000;

// NOTE: NSAI-7892 — the cut never ends on the first half of a surrogate pair
// (an emoji, astral CJK): Postgres jsonb rejects a lone surrogate, and the
// steps are written in the same row update as the finished answer, so one
// split emoji lost the whole reply.
const isHighSurrogate = (codeUnit: number): boolean =>
  codeUnit >= 0xd800 && codeUnit <= 0xdbff;

const isLowSurrogate = (codeUnit: number): boolean =>
  codeUnit >= 0xdc00 && codeUnit <= 0xdfff;

// NOTE: NSAI-7892 — a model can also send a lone surrogate itself (JSON allows
// an escaped unpaired one), which jsonb rejects the same way; it becomes
// U+FFFD, and so does U+0000, the one other code point jsonb refuses. Runs on
// the already-capped value, so it never walks a whole body.
const isRejectedByJsonb = (codeUnit: number): boolean =>
  codeUnit === 0 || isHighSurrogate(codeUnit) || isLowSurrogate(codeUnit);

const toWellFormedText = (value: string): string => {
  let wellFormed = "";
  for (let index = 0; index < value.length; index += 1) {
    const codeUnit = value.charCodeAt(index);
    if (
      isHighSurrogate(codeUnit) &&
      isLowSurrogate(value.charCodeAt(index + 1))
    ) {
      wellFormed += value.slice(index, index + 2);
      index += 1;
    } else {
      wellFormed += isRejectedByJsonb(codeUnit)
        ? "\uFFFD"
        : value.charAt(index);
    }
  }
  return wellFormed;
};

const truncateToolStepArg = (value: string): string => {
  if (value.length <= MAX_TOOL_STEP_ARG_LENGTH) return toWellFormedText(value);
  const cutAt = isHighSurrogate(value.charCodeAt(MAX_TOOL_STEP_ARG_LENGTH - 1))
    ? MAX_TOOL_STEP_ARG_LENGTH - 1
    : MAX_TOOL_STEP_ARG_LENGTH;
  return `${toWellFormedText(value.slice(0, cutAt))}…`;
};

interface IBoundedJsonWriter {
  parts: string[];
  seen: Set<object>;
  length: number;
  budget: number;
}

interface IWriteJsonProps {
  writer: IBoundedJsonWriter;
  item: unknown;
}

interface IPushJsonProps {
  writer: IBoundedJsonWriter;
  text: string;
}

interface IWriteJsonArrayProps {
  writer: IBoundedJsonWriter;
  item: unknown[];
}

interface IWriteJsonObjectProps {
  writer: IBoundedJsonWriter;
  item: object;
}

const pushJson = ({ writer, text }: IPushJsonProps): void => {
  writer.parts.push(text);
  writer.length += text.length;
};

const isJsonWriterFull = (writer: IBoundedJsonWriter): boolean =>
  writer.length >= writer.budget;

const isOmittedJsonValue = (item: unknown): boolean =>
  item === undefined || typeof item === "function" || typeof item === "symbol";

// NOTE: NSAI-7892 — a long string is sliced before it is escaped, never on the
// first half of a surrogate pair, so its JSON text matches the full one up to
// the budget.
const toBoundedJsonString = ({ writer, item }: IWriteJsonProps): string => {
  const text = String(item);
  const remaining = writer.budget - writer.length + 1;
  const end = isHighSurrogate(text.charCodeAt(remaining - 1))
    ? remaining + 1
    : remaining;
  return JSON.stringify(text.slice(0, end));
};

// NOTE: The JSON text of a scalar, or null for an array or object.
const toJsonScalar = ({ writer, item }: IWriteJsonProps): string | null => {
  if (item === null || isOmittedJsonValue(item)) return "null";
  if (typeof item === "boolean") return String(item);
  if (typeof item === "number") {
    return Number.isFinite(item) ? String(item) : "null";
  }
  if (typeof item === "bigint") {
    throw new TypeError("BigInt value can't be serialized");
  }
  if (typeof item === "string") return toBoundedJsonString({ writer, item });
  return null;
};

const writeJsonArray = ({ writer, item }: IWriteJsonArrayProps): void => {
  pushJson({ writer, text: "[" });
  for (
    let index = 0;
    index < item.length && !isJsonWriterFull(writer);
    index += 1
  ) {
    if (index > 0) pushJson({ writer, text: "," });
    writeJson({ writer, item: item[index] });
  }
  pushJson({ writer, text: "]" });
};

const writeJsonObject = ({ writer, item }: IWriteJsonObjectProps): void => {
  pushJson({ writer, text: "{" });
  let isFirst = true;
  for (const key of Object.keys(item)) {
    if (isJsonWriterFull(writer)) break;
    const entry: unknown = Reflect.get(item, key);
    if (isOmittedJsonValue(entry)) continue;
    pushJson({ writer, text: `${isFirst ? "" : ","}${JSON.stringify(key)}:` });
    isFirst = false;
    writeJson({ writer, item: entry });
  }
  pushJson({ writer, text: "}" });
};

// NOTE: NSAI-7892 — writes JSON text only until the writer's budget is full.
// Throws on a cycle or a BigInt, as JSON.stringify does.
const writeJson = ({ writer, item }: IWriteJsonProps): void => {
  if (isJsonWriterFull(writer)) return;
  const scalar = toJsonScalar({ writer, item });
  if (scalar !== null) {
    pushJson({ writer, text: scalar });
    return;
  }
  if (typeof item !== "object" || item === null) return;
  if (writer.seen.has(item)) {
    throw new TypeError("Converting circular structure");
  }
  writer.seen.add(item);
  if (Array.isArray(item)) writeJsonArray({ writer, item });
  else writeJsonObject({ writer, item });
  writer.seen.delete(item);
};

// NOTE: NSAI-7892 — mirrors the server: the fallback for an array or object
// nested deeper than MAX_TOOL_STEP_ARG_DEPTH is a bounded JSON preview. The
// text is written only until the preview budget is full, since a document
// generator's arguments can be the whole document. Up to the budget it matches
// JSON.stringify for the JSON values a model sends; on a cycle or a BigInt the
// value is dropped.
const serializeToolStepArg = (value: object): string | null => {
  const writer: IBoundedJsonWriter = {
    parts: [],
    seen: new Set<object>(),
    length: 0,
    budget: MAX_TOOL_STEP_ARG_LENGTH + 2,
  };
  try {
    writeJson({ writer, item: value });
    return writer.parts.join("");
  } catch {
    return null;
  }
};

interface IToolStepArgBudget {
  used: number;
}

interface ICompactToolStepArgValueProps {
  value: unknown;
  depth: number;
  budget: IToolStepArgBudget;
}

interface ICompactToolStepArgArrayProps {
  value: unknown[];
  depth: number;
  budget: IToolStepArgBudget;
}

interface ICompactToolStepArgObjectProps {
  value: object;
  depth: number;
  budget: IToolStepArgBudget;
}

const isToolStepArgBudgetSpent = (budget: IToolStepArgBudget): boolean =>
  budget.used >= MAX_TOOL_STEP_NESTED_ARGS_LENGTH;

// NOTE: NSAI-7892 — only text inside an array or object (depth 2 and below)
// draws on the shared budget; the top-level scalars the labels interpolate are
// never crowded out.
const toCappedToolStepArgText = ({
  value,
  depth,
  budget,
}: ICompactToolStepArgValueProps): string => {
  const capped = truncateToolStepArg(String(value));
  if (depth > 1) budget.used += capped.length;
  return capped;
};

// NOTE: NSAI-7892 — marks a value the walker drops, distinct from a nested
// `null`, which is kept.
const DROPPED_TOOL_STEP_ARG = Symbol("dropped-tool-step-arg");

interface ISetToolStepArgEntryProps {
  target: Record<string, unknown>;
  key: string;
  value: unknown;
}

// NOTE: NSAI-7892 — defines the key as an own property, so a model-sent
// `__proto__` is kept as data instead of replacing the object's prototype.
const setToolStepArgEntry = ({
  target,
  key,
  value,
}: ISetToolStepArgEntryProps): void => {
  Object.defineProperty(target, key, {
    value,
    enumerable: true,
    writable: true,
    configurable: true,
  });
};

const compactToolStepArgArray = ({
  value,
  depth,
  budget,
}: ICompactToolStepArgArrayProps): unknown[] => {
  const compacted: unknown[] = [];
  const end = Math.min(value.length, MAX_TOOL_STEP_ARG_ITEMS);
  for (
    let index = 0;
    index < end && !isToolStepArgBudgetSpent(budget);
    index += 1
  ) {
    const item = compactToolStepArgValue({
      value: value[index],
      depth: depth + 1,
      budget,
    });
    if (item !== DROPPED_TOOL_STEP_ARG) compacted.push(item);
  }
  return compacted;
};

const compactToolStepArgObject = ({
  value,
  depth,
  budget,
}: ICompactToolStepArgObjectProps): Record<string, unknown> => {
  const compacted: Record<string, unknown> = {};
  let keyCount = 0;
  for (const key of Object.keys(value)) {
    if (keyCount >= MAX_TOOL_STEP_ARG_KEYS) break;
    if (depth > 0 && isToolStepArgBudgetSpent(budget)) break;
    // NOTE: NSAI-7892 — a key is model-written text too: capped and made
    // jsonb-safe like a value, and counted against the budget below the top
    // level, so a payload of long keys stays bounded. Two long keys that share
    // their first 160 characters cap to the same text; the first one wins, so
    // a cap never overwrites a value or spends the budget twice.
    const safeKey = truncateToolStepArg(key);
    if (Object.hasOwn(compacted, safeKey)) continue;
    const item = compactToolStepArgValue({
      value: Reflect.get(value, key),
      depth: depth + 1,
      budget,
    });
    if (item !== DROPPED_TOOL_STEP_ARG) {
      if (depth > 0) budget.used += safeKey.length;
      setToolStepArgEntry({ target: compacted, key: safeKey, value: item });
      keyCount += 1;
    }
  }
  return compacted;
};

// NOTE: NSAI-7892 — mirrors the server: an array or object keeps its structure
// (each delegate subtask with its own fields) instead of collapsing into one
// capped string; every string in it is capped like a top-level one, and past
// MAX_TOOL_STEP_ARG_DEPTH a value becomes a JSON preview. A nested null is
// kept (`{ date: null }` clears a field); DROPPED_TOOL_STEP_ARG marks a
// dropped value.
const compactToolStepArgValue = ({
  value,
  depth,
  budget,
}: ICompactToolStepArgValueProps): unknown => {
  if (typeof value === "number" || typeof value === "boolean") return value;
  if (value === null) return depth > 1 ? null : DROPPED_TOOL_STEP_ARG;
  if (typeof value === "string") {
    return toCappedToolStepArgText({ value, depth, budget });
  }
  if (typeof value !== "object") return DROPPED_TOOL_STEP_ARG;
  if (depth > MAX_TOOL_STEP_ARG_DEPTH) {
    const serialized = serializeToolStepArg(value);
    return serialized === null
      ? DROPPED_TOOL_STEP_ARG
      : toCappedToolStepArgText({ value: serialized, depth, budget });
  }
  return Array.isArray(value)
    ? compactToolStepArgArray({ value, depth, budget })
    : compactToolStepArgObject({ value, depth, budget });
};

// NOTE: NSAI-5473 — a step's label interpolates short top-level scalars
// (`title`, `issueKey`, `query`). A generator's arguments can be a whole
// document, and up to MAX_TOOL_STEPS_PER_MESSAGE of them would otherwise sit in
// the query cache, so every string is capped at MAX_TOOL_STEP_ARG_LENGTH and a
// structured value is bounded as above; `message.tool` still holds the current
// call's full args.
export const compactToolStepArgs = (
  args: Record<string, unknown>,
): Record<string, unknown> =>
  compactToolStepArgObject({ value: args, depth: 0, budget: { used: 0 } });

// NOTE: NSAI-5473 — the step transcript's write side, applied by web and mobile
// to the cached message in place (like `setMessageToolCall`). A frame for a call
// already listed — the tool-call is re-sent as its arguments stream in — only
// merges the new arguments into that line; it never adds a second one.
export const appendMessageToolStep = ({
  message,
  toolCallId,
  toolName,
  args,
}: IAppendMessageToolStepProps): boolean => {
  const steps = message.toolSteps ?? [];
  const existing = steps.find((step) => step.id === toolCallId);
  if (existing) {
    message.toolSteps = steps.map((step) =>
      step === existing
        ? { ...step, args: compactToolStepArgs({ ...step.args, ...args }) }
        : step,
    );
    return true;
  }
  if (steps.length >= MAX_TOOL_STEPS_PER_MESSAGE) return false;
  message.toolSteps = [
    ...steps,
    {
      id: toolCallId,
      name: toolName,
      args: compactToolStepArgs(args),
      status: EToolStepStatus.RUNNING,
    },
  ];
  return true;
};

// NOTE: NSAI-5473 — the live tool row is redundant only while the call it shows
// is already a line of the Steps list. A progress frame with no call id (the
// presentation's slide-by-slide status) is not, so its row stays visible.
export const isToolCallShownAsStep = ({
  tool,
  steps,
}: IIsToolCallShownAsStepProps): boolean =>
  !!tool?.toolCallId && !!steps?.some((step) => step.id === tool.toolCallId);

export const setMessageToolStepStatus = ({
  message,
  toolCallId,
  status,
}: ISetMessageToolStepStatusProps): boolean => {
  const steps = message.toolSteps;
  if (!steps?.some((step) => step.id === toolCallId)) return false;
  message.toolSteps = steps.map((step) =>
    step.id === toolCallId ? { ...step, status } : step,
  );
  return true;
};

// NOTE: NSAI-5473 — the `toolCallEnd` frame's write, shared so web and mobile
// apply it from one copy.
export const setMessageToolStepStatusInPages = ({
  pages,
  uuid,
  toolCallId,
  status,
}: ISetMessageToolStepStatusInPagesProps): boolean => {
  for (const page of pages) {
    const message = page.data.find((entry) => entry.uuid === uuid);
    if (message) {
      return setMessageToolStepStatus({ message, toolCallId, status });
    }
  }
  return false;
};

// NOTE: NSAI-5473 — a step can only be running while its message is. Once the
// turn is over, one still marked RUNNING never got its result (the user
// stopped the turn, or it failed around the call), and a spinner there would
// claim work is still happening.
export const resolveToolStepDisplayStatus = ({
  status,
  isMessageFinished,
}: IResolveToolStepDisplayStatusProps): TToolStepDisplayStatus =>
  status === EToolStepStatus.RUNNING && isMessageFinished
    ? "interrupted"
    : status;

// NOTE: NSAI-5473 — a step's label is the live progress text ("Generating
// PDF…"). Once the step has ended, the trailing ellipsis would still claim work
// in progress beside a ✓, so only a running step keeps it.
const TRAILING_ELLIPSIS_PATTERN = /\s*(?:\.{3}|…)$/u;

export const formatToolStepLabel = ({
  label,
  status,
}: IFormatToolStepLabelProps): string =>
  status === EToolStepStatus.RUNNING
    ? label
    : label.replace(TRAILING_ELLIPSIS_PATTERN, "");
